import { useEffect, useMemo, useState } from "react";
import type { FieldErrors, UseFormReturn } from "react-hook-form";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";

const getFieldsInErrorKey = (errors: FieldErrors<DeckDesignerConfigurationFormValues>): string =>
  Object.keys(errors)
    .filter((fieldName) => !!errors[fieldName as keyof typeof errors])
    .sort()
    .join("|");

// Names of the fields currently in error. Subscribed outside render and kept as a joined key, so the
// host re-renders only when that set of fields changes - useFormState({ control }).errors instead
// re-renders it on every validation, error or not, and every setValue with shouldValidate validates.
export const useFieldsInError = (
  formMethods: UseFormReturn<DeckDesignerConfigurationFormValues>,
): ReadonlySet<string> => {
  const { subscribe, getErrors } = formMethods;
  const [fieldsInErrorKey, setFieldsInErrorKey] = useState(() => getFieldsInErrorKey(getErrors()));

  useEffect(() => {
    const unsubscribe = subscribe({
      formState: { errors: true },
      callback: ({ errors }) => setFieldsInErrorKey(getFieldsInErrorKey(errors ?? getErrors())),
    });
    // Catches errors set between the first render and this subscription.
    setFieldsInErrorKey(getFieldsInErrorKey(getErrors()));

    return unsubscribe;
  }, [getErrors, subscribe]);

  return useMemo(() => new Set(fieldsInErrorKey ? fieldsInErrorKey.split("|") : []), [fieldsInErrorKey]);
};
